/**
 * cngnCurrency.ts
 *
 * Centralized CNGN (Nigerian Naira stablecoin) display utilities.
 *
 * CNGN is pegged ~1:1 to the Nigerian Naira (₦). The oracle reports its
 * price in USD (~$0.00073) but users expect to see ₦ values.
 *
 * Key conversions:
 * - Token price: raw $0.00073 → show ₦1,370 ($0.00073)
 *   because ₦1,370 = 1 / 0.00073 (the USD/NGN exchange rate)
 * - USD amounts: $500 → show ₦685,000 ($500)
 *   because ₦685,000 = 500 / 0.00073
 */

import { formatAmount, formatUsd, formatUsdPrice, USD_DECIMALS, expandDecimals } from "./numbers";

// ─── Constants ────────────────────────────────────────────────────────────────

export const NAIRA_SYMBOL = "₦";
export const USD_SYMBOL = "$";
export const CNGN_TOKEN_SYMBOL = "CNGN";

/** 10^30 — the scale factor for 30-decimal bigint math */
const SCALE = expandDecimals(1, USD_DECIMALS);

// ─── Detection Helpers ────────────────────────────────────────────────────────

export function isCngnToken(symbol?: string): boolean {
    if (!symbol) return false;
    return symbol.toUpperCase() === CNGN_TOKEN_SYMBOL;
}

export function isCngnMarket(marketNameOrIndexName?: string): boolean {
    if (!marketNameOrIndexName) return false;
    return marketNameOrIndexName.toUpperCase().includes(CNGN_TOKEN_SYMBOL);
}

export function getCurrencySymbol(tokenSymbol?: string): string {
    return isCngnToken(tokenSymbol) ? NAIRA_SYMBOL : USD_SYMBOL;
}

// ─── Conversion ───────────────────────────────────────────────────────────────

/**
 * Convert a USD value (30-decimal bigint) to its Naira equivalent.
 * nairaAmount = usdAmount / cngnPriceUsd
 *
 * @returns 30-decimal bigint in Naira, or undefined if conversion impossible
 */
export function convertUsdToNaira(
    usdAmount: bigint,
    cngnPriceUsd: bigint
): bigint | undefined {
    if (cngnPriceUsd === 0n) return undefined;
    // Both are 30-decimal, so (usd * 10^30) / cngn gives a 30-decimal result
    return (usdAmount * SCALE) / cngnPriceUsd;
}

// ─── Formatting ───────────────────────────────────────────────────────────────

/**
 * Formats a CNGN token price for market/pool displays.
 *
 * The oracle reports CNGN price in USD (~$0.00073). Users expect:
 *   ₦1,370.00 ($0.00073)
 * where ₦1,370 = 1/0.00073 (the exchange rate: Naira per USD).
 *
 * When isAlreadyNairaRate is true, the price has already been inverted
 * (e.g. by invertUsdPrice or visualMultiplier) so we just swap $ for ₦
 * without computing 1/price again.
 *
 * For non-CNGN: falls through to standard formatUsdPrice().
 */
export function formatCngnPrice(
    price: bigint | undefined,
    tokenSymbol?: string,
    opts?: { visualMultiplier?: number; displayDecimals?: number; isAlreadyNairaRate?: boolean }
): string | undefined {
    if (price === undefined) return undefined;

    if (!isCngnToken(tokenSymbol)) {
        return formatUsdPrice(price, opts);
    }

    // USD display (raw price, with visualMultiplier if any)
    const usdFormatted = formatUsdPrice(price, opts);
    if (!usdFormatted) return undefined;

    if (price === 0n) return `${NAIRA_SYMBOL}0.00 (${usdFormatted})`;

    if (opts?.isAlreadyNairaRate) {
        // Price is already the Naira exchange rate (e.g. 1,355.33)
        // Just swap the $ symbol for ₦
        const nairaFormatted = usdFormatted.replace(/^\$/, NAIRA_SYMBOL);
        return nairaFormatted;
    }

    // Raw CNGN price (~$0.00073) → compute Naira exchange rate: 1 / cngnPriceUsd
    // result = (10^30 * 10^30) / price → 30-decimal bigint
    const nairaRate = (SCALE * SCALE) / price;
    const displayDecimals = opts?.displayDecimals ?? 2;
    const nairaNumber = formatAmount(nairaRate, USD_DECIMALS, displayDecimals, true);

    return `${NAIRA_SYMBOL}${nairaNumber} (${usdFormatted})`;
}

/**
 * Formats a USD amount with Naira equivalent for CNGN market contexts.
 *
 * Example: $500 paid into CNGN market → ₦685,000.00 ($500.00)
 *
 * @param usd           USD amount (30-decimal bigint)
 * @param tokenSymbol   Token symbol to detect CNGN context
 * @param cngnPriceUsd  CNGN price in USD (30-decimal bigint) — required for conversion
 * @param opts          Options passed to formatUsd
 */
export function formatCngnUsd(
    usd: bigint | undefined,
    tokenSymbol?: string,
    cngnPriceUsd?: bigint,
    opts?: {
        fallbackToZero?: boolean;
        displayDecimals?: number;
        displayPlus?: boolean;
    }
): string | undefined {
    const usdFormatted = formatUsd(usd, opts);

    if (
        !usdFormatted ||
        !isCngnToken(tokenSymbol) ||
        !cngnPriceUsd ||
        cngnPriceUsd === 0n ||
        usd === undefined
    ) {
        return usdFormatted;
    }

    // Convert USD → Naira
    const absUsd = usd < 0n ? -usd : usd;
    const nairaAmount = convertUsdToNaira(absUsd, cngnPriceUsd);
    if (nairaAmount === undefined) return usdFormatted;

    const displayDecimals = opts?.displayDecimals ?? 2;
    const nairaNumber = formatAmount(nairaAmount, USD_DECIMALS, displayDecimals, true);

    const sign = usd < 0n ? "-" : (opts?.displayPlus ? "+" : "");

    return `${sign}${NAIRA_SYMBOL}${nairaNumber} (${usdFormatted})`;
}
